import { useNotification } from '@app/AppLayout/NotificationContext';
import { PostalCodeApi } from '@app/api';
import { DataLoadErrorRow } from '@app/components/DataLoadError';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useEntity } from '@app/hooks/useEntity';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { Country } from '@app/model/Country';
import {
  PostalCodeCoverage,
  PostalCodeImportSummary,
  UNREADABLE_ROWS_WORTH_REPORTING
} from '@app/model/PostalCode';
import { useCountryStore } from '@app/store/entityStore';
import { ApiError, toApiError } from '@app/utils/ApiError';
import {
  ActionList,
  ActionListItem,
  Alert,
  AlertVariant,
  Bullseye,
  Button,
  Card,
  CardBody,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateBody,
  Icon,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Pagination,
  PaginationVariant,
  Spinner,
  Split,
  SplitItem,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip
} from '@patternfly/react-core';
import { ExclamationTriangleIcon, MapMarkerAltIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';

/**
 * The postal code reference data the address parse reads, at /admin/postalcodes.
 *
 * Two things: what is loaded, and a button to load it. That is the whole of what the service
 * exposes, and deliberately so - the table is several hundred thousand rows of published reference
 * data addressed only by (country, code), so there is no row browser here, no per-country import,
 * no delete and no editor. See model/PostalCode.
 *
 * WHY AN ADMIN CARES: where a country has no codes loaded, no postcode is ever read for an address
 * in it. The service does not guess one - it leaves the text in the address lines instead, which is
 * what stops a house number landing in the column people search on. So this page answers "why has
 * this address no postcode", and the answer is usually a row missing from the table below.
 *
 * The data is CC BY 4.0, GeoNames, and the attribution at the foot of the page is a condition of
 * showing it rather than a courtesy.
 */

/**
 * What the export carries for the countries where it is not the whole answer, keyed by alpha-2.
 *
 * Surfaced on the row because these will otherwise be reported as bugs: somebody looks up a British
 * class, sees `SW1A` in the reference data where the address says `SW1A 1AA`, and files a truncation
 * bug against the import. The import is right - the combined file is what is short.
 */
const PARTIAL_COVERAGE: Record<string, string> = {
  IE: 'The export carries the routing key only, so an Irish code can be confirmed no further than its first part - D01, not D01 W1W4.',
  GB: 'The combined export carries the leading part only - SW1A, not SW1A 1AA. The full set is a separate download and carries Royal Mail copyright.',
  NL: 'The combined export carries the leading part only. The full set is a separate download.',
  CA: 'The combined export carries the leading part only. The full set is a separate download.',
  AR: 'The export is abbreviated or partial for this country.',
  BR: 'The export is abbreviated or partial for this country.',
  CL: 'The export is abbreviated or partial for this country.',
  CN: 'The export is abbreviated or partial for this country.'
};

/**
 * Countries the GeoNames export was found to carry nothing for, by alpha-2.
 *
 * Named under the table because they cannot appear in it, and an address in one of them will never
 * carry a postcode however often the import runs. Worth saying: Tunisia is the fourth country by
 * address count in this database.
 *
 * **ALWAYS FILTERED AGAINST THE COVERAGE ACTUALLY LOADED - see `uncoveredCountryNames`, and do not
 * render this list raw.** It is an observation about one export at one moment, not a fact about the
 * world, and it was already out of date when this page first met real data: of the nine, the file
 * loaded here carries 178,171 codes for the UAE, 96,943 for Peru and 1,325 for Morocco. Printed
 * unfiltered, the note claimed no export existed for the UAE directly above a table row counting
 * its codes. Filtering also means a country dropping out of a later export is picked up on its own.
 */
const NO_EXPORT_AT_ALL: readonly string[] = ['TN', 'GR', 'AE', 'MA', 'MU', 'NG', 'PE', 'SA', 'IL'];

/**
 * Whether a run's unreadable rows say the setting names the wrong file.
 *
 * The case this is for is real and was met on the first run against a dev machine: the setting
 * pointed at the GeoNames *placenames* dump, which is also called allCountries, and 13,463,989 of
 * its 13,464,063 rows were unreadable as postal codes. A run reported as "Success" would have
 * hidden that behind a green tick.
 */
const isFileSuspect = (result: PostalCodeImportSummary): boolean =>
  result.rowsSkippedUnreadable >= UNREADABLE_ROWS_WORTH_REPORTING;

/** A coverage row joined to the country it names, so the table can show more than a code. */
interface CoverageRow extends PostalCodeCoverage {
  country?: Country;
  /** The country's name, or the bare code when this database has no country by that code. */
  label: string;
}

/**
 * Styling done inline with design tokens rather than PatternFly's utility classes.
 *
 * `pf-v6-u-*` does not work here: index.tsx loads only `base.css`, and the utilities live in a
 * separate stylesheet nothing imports - so a `pf-v6-u-text-align-end` silently does nothing, which
 * is how the first version of this table shipped with a left-aligned column of numbers. No other
 * file in this app uses one either.
 */
const SPACER_MD = 'var(--pf-t--global--spacer--md, 1rem)';
const SUBTLE = 'var(--pf-t--global--text--color--subtle, #6a6e73)';
/** A column of magnitudes reads down its right edge. */
const NUMERIC: React.CSSProperties = { textAlign: 'right' };

const columnIndex = { COUNTRY: 0, CODE: 1, CODES: 2 } as const;

const PostalCodes: React.FunctionComponent = () => {
  const { addNotification } = useNotification();
  const { items: countries } = useEntity(useCountryStore);

  const [coverage, setCoverage] = React.useState<PostalCodeCoverage[]>([]);
  /** Null until the first read finishes, so the table is not called empty before it has loaded. */
  const [loadError, setLoadError] = React.useState<ApiError | null>(null);
  const [hasLoaded, setHasLoaded] = React.useState(false);

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [isImporting, setIsImporting] = React.useState(false);
  const [summary, setSummary] = React.useState<PostalCodeImportSummary | null>(null);
  const [importError, setImportError] = React.useState<ApiError | null>(null);

  const [page, setPage] = React.useState(1);
  const { perPage, setPerPage } = useTablePreferences('postal-code-coverage', 20);
  const [sortIndex, setSortIndex] = React.useState<number | null>(null);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc' | null>(null);

  const loadCoverage = React.useCallback(async () => {
    try {
      const rows = await PostalCodeApi.getCoverage();
      setCoverage(rows);
      setLoadError(null);
    } catch (error: unknown) {
      setLoadError(toApiError(error));
    } finally {
      setHasLoaded(true);
    }
  }, []);

  React.useEffect(() => {
    void loadCoverage();
  }, [loadCoverage]);

  // --- Derived state --------------------------------------------------------------------------

  const byAlpha2 = React.useMemo(() => {
    const map = new Map<string, Country>();
    countries.forEach(country => country.alpha2 && map.set(country.alpha2, country));
    return map;
  }, [countries]);

  const rows = React.useMemo<CoverageRow[]>(
    () => coverage.map(row => {
      const country = byAlpha2.get(row.countryCode);
      return { ...row, country, label: country?.name ?? row.countryCode };
    }),
    [coverage, byAlpha2]
  );

  /**
   * Sorted, or in the order the service sent - most codes first, which is the order that answers
   * "is this file the whole thing" at a glance. An unsorted table keeps it.
   */
  const sortedRows = React.useMemo(() => {
    if (sortIndex === null || sortDirection === null) return rows;

    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      switch (sortIndex) {
        case columnIndex.CODES: return (a.codes - b.codes) * direction;
        case columnIndex.CODE: return a.countryCode.localeCompare(b.countryCode) * direction;
        default: return a.label.localeCompare(b.label) * direction;
      }
    });
  }, [rows, sortIndex, sortDirection]);

  const paginatedRows = React.useMemo(
    () => sortedRows.slice((page - 1) * perPage, (page - 1) * perPage + perPage),
    [sortedRows, page, perPage]
  );

  const totalCodes = React.useMemo(() => rows.reduce((sum, row) => sum + row.codes, 0), [rows]);

  /**
   * The known-uncovered countries, named rather than coded - and only those the loaded file really
   * has nothing for.
   *
   * The coverage answer wins over the list every time: a country with codes in the table is covered,
   * whatever was true of the export this list was written from. See NO_EXPORT_AT_ALL.
   */
  const uncoveredCountryNames = React.useMemo(() => {
    const covered = new Set(coverage.map(row => row.countryCode));
    return NO_EXPORT_AT_ALL
      .filter(code => !covered.has(code))
      .map(code => byAlpha2.get(code)?.name ?? code)
      .sort((a, b) => a.localeCompare(b));
  }, [coverage, byAlpha2]);

  const isEmpty = hasLoaded && !loadError && rows.length === 0;

  // --- The import -----------------------------------------------------------------------------

  /**
   * Runs the import, keeping the modal open and blocking for as long as it takes.
   *
   * It is a synchronous minute or two on the combined file and there is no job to poll, so the
   * modal becomes the progress state rather than closing on a promise nobody can see. **No
   * automatic retry on failure**: a retry mid-import queues a second full load of the same file.
   */
  const onImport = async () => {
    setIsImporting(true);
    setSummary(null);
    setImportError(null);
    try {
      const result = await PostalCodeApi.runImport();
      setSummary(result);
      setConfirmOpen(false);
      // The toast agrees with the panel rather than saying "loaded" over the top of it: a run that
      // read thirteen million rows and wrote none is not a success to be congratulated on, and the
      // toast is the part that survives the reader scrolling away.
      addNotification(
        isFileSuspect(result) ? 'Postal codes loaded, with rows that could not be read' : 'Postal codes loaded',
        isFileSuspect(result) ? AlertVariant.warning : AlertVariant.success,
        `${result.countriesLoaded} countries, ${result.rowsLoaded.toLocaleString()} codes.`
      );
      await loadCoverage();
    } catch (error: unknown) {
      // Shown in the page rather than only as a toast: the service writes these messages for a
      // person to act on - they name the setting, or the absolute path it tried - and a toast takes
      // them away again before that can be done.
      const apiError = toApiError(error);
      setImportError(apiError);
      setConfirmOpen(false);
      addNotification('The postal code import was refused', AlertVariant.danger, apiError.message);
    } finally {
      setIsImporting(false);
    }
  };

  // --- Render helpers -------------------------------------------------------------------------

  const buildPagination = (variant: 'top' | 'bottom') => (
    <Pagination
      isCompact
      itemCount={sortedRows.length}
      page={page}
      perPage={perPage}
      onSetPage={(_e, newPage) => setPage(newPage)}
      onPerPageSelect={(_e, newPerPage, newPage) => { setPerPage(newPerPage); setPage(newPage); }}
      variant={variant as PaginationVariant}
      isStatic
    />
  );

  const sortProps = (index: number) => ({
    sortBy: { index: sortIndex ?? undefined, direction: sortDirection ?? undefined },
    onSort: (_e: unknown, i: number, d: 'asc' | 'desc') => { setSortIndex(i); setSortDirection(d); },
    columnIndex: index
  });

  const renderNote = (row: CoverageRow) => {
    const note = PARTIAL_COVERAGE[row.countryCode];
    if (!note) return null;
    return (
      <Tooltip content={note}>
        <span className="tooltipped-text">Partial</span>
      </Tooltip>
    );
  };

  /**
   * The result of the last import, every number of it.
   *
   * Not reduced to "Success" on purpose: `rowsSkippedUnreadable` in the thousands is how somebody
   * finds out they pointed the setting at the wrong file, and it is the only place that shows.
   */
  const renderSummary = (result: PostalCodeImportSummary) => {
    const suspect = isFileSuspect(result);
    return (
      <Alert
        isInline
        variant={suspect ? AlertVariant.warning : AlertVariant.success}
        title={suspect ? 'Postal codes loaded, with unreadable rows worth checking' : 'Postal codes loaded'}
        actionClose={<Button variant="link" isInline onClick={() => setSummary(null)}>Dismiss</Button>}
      >
        {/* A wide term column: two of these labels carry a tooltip, and at the default width they
            wrap onto three lines, which puts a dotted underline through the middle of a word. */}
        <DescriptionList isHorizontal isCompact termWidth="14rem">
          <DescriptionListGroup>
            <DescriptionListTerm>File read</DescriptionListTerm>
            <DescriptionListDescription>
              <code>{result.file}</code>
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Countries loaded</DescriptionListTerm>
            <DescriptionListDescription>{result.countriesLoaded.toLocaleString()}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Rows read</DescriptionListTerm>
            <DescriptionListDescription>{result.rowsRead.toLocaleString()}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Codes written</DescriptionListTerm>
            <DescriptionListDescription>{result.rowsLoaded.toLocaleString()}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>
              <Tooltip content="Rows for a country this database does not have. The combined export carries every country GeoNames publishes, so this is expected to be very large and is not an error.">
                <span className="tooltipped-text">Skipped, unknown country</span>
              </Tooltip>
            </DescriptionListTerm>
            <DescriptionListDescription>{result.rowsSkippedUnknownCountry.toLocaleString()}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>
              <Tooltip content="Malformed lines. A handful is normal; thousands usually means the file is not the postal code export.">
                <span className="tooltipped-text">Skipped, unreadable</span>
              </Tooltip>
            </DescriptionListTerm>
            <DescriptionListDescription>
              {result.rowsSkippedUnreadable.toLocaleString()}
              {suspect && (
                <>
                  {' '}
                  <Icon status="warning"><ExclamationTriangleIcon /></Icon>{' '}
                  Worth checking that <code>cas.postal-code.source-file</code> names the postal code export.
                </>
              )}
            </DescriptionListDescription>
          </DescriptionListGroup>
        </DescriptionList>
      </Alert>
    );
  };

  return (
    <React.Fragment>
      {/* Header */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">Postal Codes</Title>
          </SplitItem>
          <SplitItem isFilled />
          <SplitItem>
            <ActionList>
              <ActionListItem>
                <Button variant="primary" onClick={() => setConfirmOpen(true)} isDisabled={isImporting}>
                  Load postal codes
                </Button>
              </ActionListItem>
              <ActionListItem>
                <PageActionsMenu onRefresh={loadCoverage} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
        <Content component="p">
          Which countries an address can have its postcode read for. Where a country has no codes
          loaded, the service reads no postcode and leaves the text in the address lines - it does
          not guess one.
        </Content>
      </PageSection>

      {/* The last import's result, and the refusal of one */}
      {(summary || importError) && (
        <PageSection hasBodyWrapper isWidthLimited>
          {summary && renderSummary(summary)}
          {importError && (
            <Alert
              isInline
              variant={AlertVariant.danger}
              title="The postal code import did not run"
              actionClose={<Button variant="link" isInline onClick={() => setImportError(null)}>Dismiss</Button>}
            >
              {/* The service's own words. They name what to do - the setting, or the path it tried -
                  and a message written here instead would only be able to say that something failed. */}
              <Content component="p">{importError.message}</Content>
              <Content component="p">
                Nothing was changed. The import loads in one transaction and commits once, so the
                table is exactly as it was and running it again is safe.
              </Content>
            </Alert>
          )}
        </PageSection>
      )}

      {/* Coverage */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Card>
          <CardBody>
            {isEmpty ? (
              <Bullseye>
                <EmptyState
                  headingLevel="h4"
                  icon={MapMarkerAltIcon}
                  titleText="No postal code reference data is loaded"
                >
                  <EmptyStateBody>
                    <Content component="p">
                      This is the normal state of a fresh install, and a working one. Addresses are
                      still imported and still readable - what is missing is the lookup that reads a
                      postcode out of one, so postcodes are left empty rather than guessed at.
                    </Content>
                    <Content component="p">
                      Download the GeoNames postal code export, put it where the service can read
                      it, point <code>cas.postal-code.source-file</code> at it, and use{' '}
                      <b>Load postal codes</b> above.
                    </Content>
                  </EmptyStateBody>
                </EmptyState>
              </Bullseye>
            ) : (
              <React.Fragment>
                <Toolbar id="postal-code-coverage-toolbar">
                  <ToolbarContent>
                    <ToolbarItem>
                      <Content component="p">
                        <b>{rows.length.toLocaleString()}</b> countries covered,{' '}
                        <b>{totalCodes.toLocaleString()}</b> codes in total.
                      </Content>
                    </ToolbarItem>
                    <ToolbarItem variant="pagination">{buildPagination('top')}</ToolbarItem>
                  </ToolbarContent>
                </Toolbar>

                <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset-lg))', minHeight: 0 }}>
                  <InnerScrollContainer>
                    <Table aria-label="Postal code coverage" isStickyHeader variant="compact" gridBreakPoint="">
                      <Thead>
                        <Tr>
                          <Th width={10}>Flag</Th>
                          <Th sort={sortProps(columnIndex.COUNTRY)}>Country</Th>
                          <Th sort={sortProps(columnIndex.CODE)}>Code</Th>
                          {/* Right-aligned: this column exists to be compared down its length, and
                              206,942 against 1 does not read at a glance ragged-left. */}
                          <Th sort={sortProps(columnIndex.CODES)} modifier="nowrap" style={NUMERIC}>Codes loaded</Th>
                          <Th>Coverage</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {paginatedRows.map(row => (
                          <Tr key={row.countryCode}>
                            <Td dataLabel="Flag"><span style={{ fontSize: '1.5rem' }}>{row.country?.flag}</span></Td>
                            <Td dataLabel="Country"><b>{row.label}</b></Td>
                            <Td dataLabel="Code">{row.countryCode}</Td>
                            <Td dataLabel="Codes loaded" style={NUMERIC}>{row.codes.toLocaleString()}</Td>
                            <Td dataLabel="Coverage">{renderNote(row)}</Td>
                          </Tr>
                        ))}
                        {loadError && rows.length === 0 && (
                          <DataLoadErrorRow
                            colSpan={5}
                            error={loadError}
                            entityLabel="postal code coverage"
                            onRetry={loadCoverage}
                          />
                        )}
                      </Tbody>
                    </Table>
                  </InnerScrollContainer>
                </OuterScrollContainer>
                {buildPagination('bottom')}
              </React.Fragment>
            )}

            {/* Why a country can be missing from the table above for good. */}
            {uncoveredCountryNames.length > 0 && (
              <Alert
                isInline
                isPlain
                variant={AlertVariant.info}
                title="Countries the export does not cover at all"
                style={{ marginTop: SPACER_MD }}
              >
                GeoNames publishes no postal code export for {uncoveredCountryNames.join(', ')}. No
                postcode will ever be read for an address in one of them, however often the import is
                run, and that is not a fault to report.
              </Alert>
            )}

            {/* A condition of showing the data, not a courtesy. */}
            <Content component="p" style={{ marginTop: SPACER_MD, fontSize: 'var(--pf-t--global--font--size--sm, 0.875rem)', color: SUBTLE }}>
              Postal code data from{' '}
              <Button variant="link" isInline component="a" href="https://www.geonames.org/" target="_blank" rel="noreferrer">
                GeoNames
              </Button>
              , licensed under CC BY 4.0.
            </Content>
          </CardBody>
        </Card>
      </PageSection>

      {/* Confirmation, which becomes the progress state rather than closing on a promise. */}
      <Modal
        variant={ModalVariant.small}
        isOpen={confirmOpen}
        onClose={() => !isImporting && setConfirmOpen(false)}
        aria-label="Confirm loading the postal codes"
      >
        <ModalHeader title={isImporting ? 'Loading postal codes' : 'Load postal codes?'} />
        <ModalBody>
          {isImporting ? (
            <Bullseye>
              <EmptyState headingLevel="h4" icon={Spinner} titleText="Loading the postal code export">
                <EmptyStateBody>
                  This takes a minute or two and cannot be interrupted. Leave this open - closing the
                  page will not stop the import, but you will not see what it did.
                </EmptyStateBody>
              </EmptyState>
            </Bullseye>
          ) : (
            <React.Fragment>
              <Content component="p">
                This <b>replaces the postal codes of every country the file carries</b>. Countries the
                file does not mention keep the codes they have.
              </Content>
              <Content component="p">
                The file is the one named by <code>cas.postal-code.source-file</code> on the server -
                it is part of the deployment and cannot be chosen here.
              </Content>
              <Content component="p">
                It runs in one transaction: if it fails, nothing changes.
              </Content>
            </React.Fragment>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={onImport} isDisabled={isImporting} isLoading={isImporting}>
            {isImporting ? 'Loading...' : 'Load postal codes'}
          </Button>
          <Button variant="link" onClick={() => setConfirmOpen(false)} isDisabled={isImporting}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export { PostalCodes };
